import React, { useState } from "react";
import client, { tryParseJSON } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ScrollText, Save } from "lucide-react";

export default function Dictionary() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [word, setWord] = useState("");
  const [strong, setStrong] = useState("");
  const [busy, setBusy] = useState(false);
  const [entry, setEntry] = useState(null);
  const [raw, setRaw] = useState("");

  const lookup = async (e) => {
    e?.preventDefault();
    if (!word.trim()) return;
    if (!user) { toast.error("Sign in to use Vine's dictionary"); nav("/login"); return; }
    setBusy(true); setEntry(null); setRaw("");
    try {
      const r = await client.post("/study/vines", { word: word.trim(), strong: strong.trim() || null });
      const parsed = tryParseJSON(r.data.raw);
      if (parsed) setEntry(parsed); else setRaw(r.data.raw);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!entry) return;
    try {
      await client.post("/studies", {
        title: `${entry.headword || word} — Vine's`,
        kind: "dictionary",
        payload: entry,
      });
      toast.success("Study saved");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-10" data-testid="dictionary-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#78350F] mb-4">Vine's-Style Dictionary</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-stone-900 mb-3">Expository entries.</h1>
        <p className="text-stone-600 max-w-2xl">
          Look up a biblical word and receive a scholarly entry in the style of W.E. Vine's
          Expository Dictionary — meanings, senses, and how the word is used in Scripture.
          Entries are AI-composed and should be verified against primary sources for critical study.
        </p>
      </div>

      <form onSubmit={lookup} className="border-t border-b hairline py-8 grid md:grid-cols-3 gap-6 items-end">
        <div className="md:col-span-2">
          <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">Word</label>
          <input value={word} onChange={(e) => setWord(e.target.value)} placeholder="e.g. agape, chesed, righteousness"
            data-testid="dict-word"
            className="w-full bg-transparent border-b border-stone-300 focus:border-[#9A3B3B] focus:outline-none py-2 text-3xl font-serif-display" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">Strong's (optional)</label>
          <input value={strong} onChange={(e) => setStrong(e.target.value)} placeholder="G26 or H2617"
            data-testid="dict-strong"
            className="w-full bg-transparent border-b border-stone-300 focus:border-[#9A3B3B] focus:outline-none py-2 text-lg font-mono" />
        </div>
        <div className="md:col-span-3">
          <button type="submit" disabled={busy} data-testid="dict-submit"
            className="bg-[#9A3B3B] hover:bg-[#7F2F2F] text-white px-8 py-3 tracking-wide transition-colors disabled:opacity-60 flex items-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ScrollText size={16} strokeWidth={1.5} />}
            Compose entry
          </button>
        </div>
      </form>

      {busy && <div className="text-center py-12 text-stone-500"><Loader2 className="mx-auto animate-spin" /> <div className="mt-3 italic">Consulting the lexicons...</div></div>}

      {entry && (
        <article className="border hairline bg-[#FDFBF7] p-8 md:p-12 space-y-8" data-testid="dict-entry">
          <header className="border-b hairline pb-6 flex items-start justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{entry.language} &middot; {entry.part_of_speech}</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">{entry.headword}</h2>
              {entry.original && (
                <div className="font-serif-display text-3xl text-[#9A3B3B] mt-2" style={{ fontFamily: "'SBL Hebrew','SBL Greek','Cormorant Garamond',serif" }}>
                  {entry.original} <span className="italic text-stone-600 text-xl">{entry.transliteration}</span>
                </div>
              )}
              {entry.strongs && <div className="font-mono text-xs text-stone-500 mt-2">Strong's {entry.strongs}</div>}
            </div>
            <button onClick={save} data-testid="dict-save"
              className="text-sm text-[#2E4F4F] hover:text-[#213A3A] flex items-center gap-1 border border-[#2E4F4F] px-3 py-1.5">
              <Save size={13} strokeWidth={1.5} /> Save
            </button>
          </header>

          {entry.core_meaning && (
            <section>
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">Core meaning</div>
              <p className="font-serif-display text-xl md:text-2xl text-stone-800 leading-relaxed italic">{entry.core_meaning}</p>
            </section>
          )}

          {entry.senses?.length > 0 && (
            <section>
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-4">Senses</div>
              <ol className="space-y-4 list-none">
                {entry.senses.map((s, i) => (
                  <li key={i} className="border-l-2 border-[#9A3B3B]/30 pl-4">
                    <div className="font-serif-display text-lg text-stone-900">{i + 1}. {s.label}</div>
                    <div className="text-stone-700 leading-relaxed">{s.gloss}</div>
                    {s.example_reference && <div className="text-xs text-stone-500 mt-1 font-mono">{s.example_reference}</div>}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {entry.usage_notes && (
            <section>
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">Usage notes</div>
              <p className="text-stone-700 leading-relaxed">{entry.usage_notes}</p>
            </section>
          )}

          {entry.related_words?.length > 0 && (
            <section className="border-t hairline pt-6">
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">Related words</div>
              <div className="flex flex-wrap gap-2">
                {entry.related_words.map((w) => (
                  <span key={w} className="text-sm border hairline px-3 py-1 font-serif-display">{w}</span>
                ))}
              </div>
            </section>
          )}
        </article>
      )}

      {raw && !entry && (
        <pre className="border hairline bg-[#FDFBF7] p-6 text-sm whitespace-pre-wrap text-stone-700">{raw}</pre>
      )}
    </div>
  );
}
